// Ren tegnelogikk (ingen React), delt mellom tegneflaten, PDF-eksporten og
// miniatyrene, slik at det eleven ser og det som havner i PDF-en alltid blir likt.

export const hexToRgba = (hex) => {
  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  }
  return [r, g, b, 255];
};

const distSq = (data, i, c) => {
  const dr = data[i] - c[0];
  const dg = data[i + 1] - c[1];
  const db = data[i + 2] - c[2];
  return dr * dr + dg * dg + db * db;
};

const FILL_TOLERANCE_SQ = 30 * 30;
const SAME_COLOR_SQ = 10 * 10;
const LINE_TOLERANCE_SQ = 30 * 30;
// Kantutvidelse: hvor langt (i piksler) fargen kan gå inn i de grå kantpikslene
// langs strekene, og hvilke lysstyrker i originaltegningen som regnes som kant.
const EDGE_REACH = 2;
const EDGE_MAX_LUMA = 225; // lysere enn dette er «papir», ikke kant
const EDGE_MIN_LUMA = 60;  // mørkere enn dette er selve streken: stopp her

const luma = (data, i) => (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;

// Scanline-fyll på et ImageData-lignende objekt ({ data, width, height }).
// Fyller det 4-sammenhengende området av piksler som ligner startpikselen.
// Med base (originaltegningen, samme størrelse) fylles også de grå kantpikslene
// langs strekene, blandet med strekens mørkhet, så det ikke blir en hvit stripe
// mellom fargen og streken. Returnerer false når det ikke er noe å fylle.
export const floodFill = (imageData, startX, startY, rgba, base = null) => {
  const { data, width, height } = imageData;
  if (startX < 0 || startY < 0 || startX >= width || startY >= height) return false;

  const startPos = (startY * width + startX) * 4;
  const target = [data[startPos], data[startPos + 1], data[startPos + 2], data[startPos + 3]];
  if (distSq(data, startPos, rgba) < SAME_COLOR_SQ) return false;
  if (target[3] === 0 || distSq(data, startPos, [0, 0, 0]) < LINE_TOLERANCE_SQ) return false;

  const seen = new Uint8Array(width * height);
  const matches = (x, y) => {
    const p = y * width + x;
    return !seen[p] && distSq(data, p * 4, target) < FILL_TOLERANCE_SQ;
  };

  const stack = [startX, startY];
  const filledRows = [];
  while (stack.length > 0) {
    const y = stack.pop();
    const x = stack.pop();
    if (!matches(x, y)) continue;

    let left = x;
    while (left > 0 && matches(left - 1, y)) left--;
    let right = x;
    while (right < width - 1 && matches(right + 1, y)) right++;

    for (let cx = left; cx <= right; cx++) {
      const p = y * width + cx;
      seen[p] = 1;
      data[p * 4] = rgba[0];
      data[p * 4 + 1] = rgba[1];
      data[p * 4 + 2] = rgba[2];
      data[p * 4 + 3] = rgba[3];
    }
    if (base) filledRows.push(y, left, right);

    // Legg ett frø per sammenhengende strekning i radene over og under
    for (const ny of [y - 1, y + 1]) {
      if (ny < 0 || ny >= height) continue;
      let inRun = false;
      for (let cx = left; cx <= right; cx++) {
        if (matches(cx, ny)) {
          if (!inRun) { stack.push(cx, ny); inRun = true; }
        } else {
          inRun = false;
        }
      }
    }
  }

  if (base) fillEdges(data, width, height, seen, filledRows, rgba, base.data);
  return true;
};

// Går EDGE_REACH piksler ut fra det fylte området, gjennom kantpiksler i
// originaltegningen, og farger dem som «fargen ganget med strekens gråtone».
const fillEdges = (data, width, height, seen, filledRows, rgba, baseData) => {
  let frontier = [];
  for (let i = 0; i < filledRows.length; i += 3) {
    const y = filledRows[i];
    for (let x = filledRows[i + 1]; x <= filledRows[i + 2]; x++) frontier.push(y * width + x);
  }
  for (let step = 0; step < EDGE_REACH; step++) {
    const next = [];
    for (const p of frontier) {
      const x = p % width;
      const y = (p - x) / width;
      const neighbours = [x > 0 ? p - 1 : -1, x < width - 1 ? p + 1 : -1, y > 0 ? p - width : -1, y < height - 1 ? p + width : -1];
      for (const q of neighbours) {
        if (q < 0 || seen[q]) continue;
        const l = luma(baseData, q * 4);
        if (l >= EDGE_MAX_LUMA || l < EDGE_MIN_LUMA) continue;
        seen[q] = 1;
        const shade = l / 255;
        data[q * 4] = Math.round(rgba[0] * shade);
        data[q * 4 + 1] = Math.round(rgba[1] * shade);
        data[q * 4 + 2] = Math.round(rgba[2] * shade);
        data[q * 4 + 3] = 255;
        next.push(q);
      }
    }
    frontier = next;
  }
};

// Eldre lagrede streker har bare brushSize; nye har width i bildepiksler.
export const strokeWidth = (action) => action.width ?? action.brushSize ?? 5;

const tracePath = (ctx, points, width) => {
  ctx.beginPath();
  if (points.length === 1) {
    ctx.arc(points[0].x, points[0].y, width / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke();
};

export const drawStroke = (ctx, action) => {
  if (!action.points || action.points.length === 0) return;
  ctx.fillStyle = action.color;
  ctx.strokeStyle = action.color;
  tracePath(ctx, action.points, strokeWidth(action));
};

// Viskelær: fjerner alt langs streken og viser originaltegningen der igjen.
export const drawErase = (ctx, action, baseImage) => {
  if (!action.points || action.points.length === 0) return;
  ctx.save();
  ctx.globalCompositeOperation = 'destination-out';
  ctx.fillStyle = '#000';
  ctx.strokeStyle = '#000';
  tracePath(ctx, action.points, strokeWidth(action));
  ctx.globalCompositeOperation = 'destination-over';
  ctx.drawImage(baseImage, 0, 0);
  ctx.restore();
};

// Originaltegningens piksler, hentet én gang per bilde
const baseDataCache = new WeakMap();
export const getBase = (image) => {
  if (!baseDataCache.has(image)) {
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(image, 0, 0);
    baseDataCache.set(image, { image, data: ctx.getImageData(0, 0, canvas.width, canvas.height).data });
  }
  return baseDataCache.get(image);
};

// Tegner handlingene oppå det som allerede er på lerretet. Påfølgende fyllinger
// deler én getImageData/putImageData, som er den dyre delen.
export const applyActions = (ctx, actions, base) => {
  const { width, height } = ctx.canvas;
  let imageData = null;
  const flush = () => {
    if (imageData) { ctx.putImageData(imageData, 0, 0); imageData = null; }
  };
  for (const action of actions) {
    if (action.type === 'fill') {
      if (typeof action.x !== 'number' || typeof action.y !== 'number') continue;
      if (!imageData) imageData = ctx.getImageData(0, 0, width, height);
      floodFill(imageData, Math.round(action.x), Math.round(action.y), hexToRgba(action.color), base);
    } else if (action.type === 'draw') {
      flush();
      drawStroke(ctx, action);
    } else if (action.type === 'erase') {
      flush();
      drawErase(ctx, action, base.image);
    } else if (action.type === 'clear') {
      imageData = null;
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(base.image, 0, 0);
    }
  }
  flush();
};

export const loadImage = (url) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error(`Kunne ikke laste bildet ${url}`));
  image.src = url;
});

// Brukes av PDF-eksporten og miniatyrene: tegner en side på et eget lerret.
export const renderPage = async (imageUrl, actions) => {
  const image = await loadImage(imageUrl);
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(image, 0, 0);
  applyActions(ctx, actions || [], getBase(image));
  return canvas;
};
